import { useEffect, useRef, RefObject } from "react";

interface UsePopoverOptions {
  /** Whether the popover is currently open. No listeners attach while false. */
  isOpen: boolean;
  /** Called on an outside click or Escape press. Put any extra close-guard logic in here. */
  onClose: () => void;
  /** Skip the Escape listener (default: false — Escape closes by default). */
  disableEscape?: boolean;
  /**
   * Extra elements that also count as "inside" for the outside-click check.
   * Needed when the popover is rendered through a portal (so it isn't a DOM
   * descendant of its trigger) — pass the trigger's ref here so clicking it
   * doesn't immediately re-close the popover.
   */
  extraRefs?: RefObject<HTMLElement | null>[];
}

/**
 * Shared outside-click + Escape handling for dropdowns/popovers/modals,
 * consolidating the ~12+ near-identical hand-rolled versions of this same
 * `useRef` + `mousedown` listener across the app (see ADMIN_PANEL_AUDIT.md
 * §3.11 / §7.1). Attach the returned ref to the popover's outermost element.
 */
export function usePopover<T extends HTMLElement = HTMLDivElement>({
  isOpen,
  onClose,
  disableEscape = false,
  extraRefs,
}: UsePopoverOptions): RefObject<T> {
  const ref = useRef<T>(null);

  // Read the latest extraRefs inside the handler without making the (possibly
  // freshly-allocated each render) array a listener dependency.
  const extraRefsRef = useRef(extraRefs);
  extraRefsRef.current = extraRefs;

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (ref.current?.contains(target)) return;
      if (extraRefsRef.current?.some((r) => r.current?.contains(target))) return;
      onClose();
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    if (!disableEscape) document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (!disableEscape) document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose, disableEscape]);

  return ref;
}
